import React, { useState, useEffect, useMemo } from 'react';
import { AnimatedCurrency } from '../common/AnimatedNumber';
import { EXPANSION_PLAN, calculateUserDividends, CALCULATION_CONSTANTS } from '../../lib/services/calculationService';

interface DividendJourneyTimelineProps {
  userShares: number;
  goldPricePerKg?: number;
}

/**
 * Animated timeline visualization showing dividend growth as mining plants come online
 */
export const DividendJourneyTimeline: React.FC<DividendJourneyTimelineProps> = ({
  userShares,
  goldPricePerKg = CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG
}) => {
  const [activeYear, setActiveYear] = useState<number | null>(null);
  const [_isAnimating, setIsAnimating] = useState(false);

  // Calculate dividends for each year
  const yearlyData = useMemo(() => {
    // Convert EXPANSION_PLAN object to array format
    return Object.entries(EXPANSION_PLAN).map(([yearStr, data]) => {
      const year = parseInt(yearStr);
      const dividends = calculateUserDividends(userShares, year);
      return {
        year,
        plants: data.plants,
        dividend: dividends.userAnnualDividend,
        cumulativeDividend: 0, // Will be calculated below
        milestone: data.plants >= 100 ? 'Full Scale' : data.plants >= 50 ? 'Major Expansion' : 'Growth Phase'
      };
    });
  }, [userShares, goldPricePerKg]);

  // Calculate cumulative dividends
  const dataWithCumulative = useMemo(() => {
    let cumulative = 0;
    return yearlyData.map(item => {
      cumulative += item.dividend;
      return { ...item, cumulativeDividend: cumulative };
    });
  }, [yearlyData]);

  // Find max dividend for scaling
  const maxDividend = useMemo(() => {
    return Math.max(...yearlyData.map(y => y.dividend));
  }, [yearlyData]);

  // Auto-animate through years on mount
  useEffect(() => {
    setIsAnimating(true);
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex < dataWithCumulative.length) {
        setActiveYear(dataWithCumulative[currentIndex].year);
        currentIndex++;
      } else {
        clearInterval(interval);
        setIsAnimating(false);
      }
    }, 800);

    return () => clearInterval(interval);
  }, [dataWithCumulative]);

  const getBarHeight = (dividend: number) => {
    return maxDividend > 0 ? (dividend / maxDividend) * 100 : 0;
  };

  return (
    <div className="dividend-journey-timeline">
      <div className="timeline-header">
        <h3>📈 Your Dividend Journey</h3>
        <p>Estimated dividends as mining operations scale up</p>
      </div>

      <div className="timeline-visualization">
        {/* Year bars */}
        <div className="timeline-bars">
          {dataWithCumulative.map((item, index) => {
            const isActive = activeYear === item.year;
            const isPast = activeYear !== null && item.year < activeYear;
            const barHeight = getBarHeight(item.dividend);
            
            return (
              <div 
                key={item.year}
                className={`timeline-bar-container ${isActive ? 'active' : ''} ${isPast ? 'past' : ''}`}
                onClick={() => setActiveYear(item.year)}
              >
                <div className="bar-value">
                  <AnimatedCurrency value={item.dividend} />
                </div>
                <div 
                  className="bar"
                  style={{ 
                    height: `${barHeight}%`,
                    animationDelay: `${index * 0.15}s`
                  }}
                >
                  <div className="bar-fill" />
                </div>
                <div className="bar-label">
                  <div className="year">{item.year}</div>
                  <div className="plants">{item.plants} 🏭</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Connecting line */}
        <div className="timeline-line" />
      </div>

      {/* Active year details */}
      {activeYear && (
        <div className="timeline-details">
          {dataWithCumulative.filter(d => d.year === activeYear).map(item => (
            <div key={item.year} className="detail-card">
              <div className="detail-header">
                <span className="detail-year">{item.year}</span>
                <span className="detail-milestone">{item.milestone}</span>
              </div>
              <div className="detail-stats">
                <div className="detail-stat">
                  <span className="stat-label">Operating Plants</span>
                  <span className="stat-value">{item.plants} modular plants</span>
                </div>
                <div className="detail-stat">
                  <span className="stat-label">Annual Dividend</span>
                  <span className="stat-value highlight">
                    <AnimatedCurrency value={item.dividend} />
                  </span>
                </div>
                <div className="detail-stat">
                  <span className="stat-label">Cumulative Earnings</span>
                  <span className="stat-value">
                    <AnimatedCurrency value={item.cumulativeDividend} />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Journey summary */}
      <div className="journey-summary">
        <div className="summary-icon">🎯</div>
        <div className="summary-text">
          By 2030, your {userShares.toLocaleString()} shares are estimated to yield{' '}
          <strong><AnimatedCurrency value={dataWithCumulative[dataWithCumulative.length - 1]?.cumulativeDividend || 0} /></strong>{' '}
          in total dividends over 5 years, based on projected mining production.
        </div>
      </div>
    </div>
  );
};

export default DividendJourneyTimeline;

